import { useMemo } from "react";
import { DataStudioJobTypeEnum } from "~/graphql/supergraphEnums";
import {
  useListCrmImportJobsQuery,
  useGetCrmImportJobValidationQuery,
  useGetCrmImportJobConfigQuery,
} from "~/graphql/supergraphHooks";
import {
  type DataStudioJobStatusEnum,
  type ListCrmImportJobsQueryVariables,
  type GetCrmImportJobValidationQueryVariables,
  type GetCrmImportJobConfigQueryVariables,
} from "~/graphql/supergraphTypes";
import { parseCrmJobConfig } from "../utils/typeUtils";
import { CrmImportJob, CrmImportJobHookOptions, ValidationSummary } from "./types";

export const useCrmImportJobDetails = (jobId: string, options?: CrmImportJobHookOptions) => {
  const { skip = false, pollInterval, onCompleted, onError } = options || {};

  const variables: ListCrmImportJobsQueryVariables = {
    limit: 1,
    filters: {
      jobId,
      jobType: DataStudioJobTypeEnum.CrmImport,
    },
  };

  const queryResult = useListCrmImportJobsQuery({
    variables,
    skip: skip || !jobId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    pollInterval,
    onCompleted,
    onError,
  });

  const transformedData = useMemo(() => {
    const response = queryResult.data?.dataStudioListOnboardingJobs;

    if ((response && !response.success) || !response?.jobs?.length) {
      return undefined;
    }

    const rawJob = response.jobs[0];

    const parsedJobConfig = rawJob.jobConfig ? parseCrmJobConfig(rawJob.jobConfig) : {};

    return {
      ...rawJob,
      parsedJobConfig,
      customerName: rawJob.arenaDomain,
      onboardingManager: {
        name: rawJob.createdBy || "Unknown",
      },
      toolValidations: [], // Initialize with empty array as this query doesn't fetch validation data
    } as CrmImportJob;
  }, [queryResult.data]);

  // Create a combined error that includes business logic errors
  const combinedError = useMemo(() => {
    if (queryResult.error) {
      return queryResult.error;
    }

    const response = queryResult.data?.dataStudioListOnboardingJobs;
    if (response && !response.success && response.errors?.length > 0) {
      const businessError = new Error(response.errors[0]);
      businessError.name = "BusinessLogicError";
      return businessError;
    }

    return undefined;
  }, [queryResult.error, queryResult.data]);

  return {
    ...queryResult,
    data: transformedData,
    job: transformedData,
    error: combinedError,
  };
};

export const useCrmImportJobValidation = (jobId: string, options?: CrmImportJobHookOptions) => {
  const { skip = false, pollInterval, onCompleted, onError } = options || {};

  const variables: GetCrmImportJobValidationQueryVariables = {
    limit: 1,
    filters: {
      jobId,
      jobType: DataStudioJobTypeEnum.CrmImport,
    },
  };

  const queryResult = useGetCrmImportJobValidationQuery({
    variables,
    skip: skip || !jobId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    pollInterval,
    onCompleted,
    onError,
  });

  const validationData = useMemo(() => {
    if (!queryResult.data?.dataStudioListOnboardingJobs?.jobs?.length) {
      return undefined;
    }

    const job = queryResult.data.dataStudioListOnboardingJobs.jobs[0];
    return {
      jobId: job.jobId,
      jobStatus: job.jobStatus,
      substate: job.substate,
      arenaDomain: job.arenaDomain,
      arenaUserEmail: job.arenaUserEmail,
      environment: job.environment,
      createdAt: job.createdAt,
      validationStats: job.validationStats,
      toolValidations: job.toolValidations,
      hasValidationErrors: job.toolValidations.some((tool) => tool.stats.errorCount > 0),
      hasValidationWarnings: job.toolValidations.some((tool) => tool.stats.warningCount > 0),
      isValidationComplete: job.toolValidations.every((tool) => tool.status === "COMPLETED"),
      totalIssues: job.toolValidations.reduce((total, tool) => total + tool.issues.length, 0),
    } as ValidationSummary;
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: validationData,
    validation: validationData,
  };
};

export const useCrmImportJobConfig = (jobId: string, options?: CrmImportJobHookOptions) => {
  const { skip = false, pollInterval, onCompleted, onError } = options || {};

  const variables: GetCrmImportJobConfigQueryVariables = {
    limit: 1,
    filters: {
      jobId,
      jobType: DataStudioJobTypeEnum.CrmImport,
    },
  };

  const queryResult = useGetCrmImportJobConfigQuery({
    variables,
    skip: skip || !jobId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    pollInterval,
    onCompleted,
    onError,
  });

  const jobConfigData = useMemo(() => {
    if (!queryResult.data?.dataStudioListOnboardingJobs?.jobs?.length) {
      return undefined;
    }

    const job = queryResult.data.dataStudioListOnboardingJobs.jobs[0];

    // Parse the job config JSON string using type-safe parsing
    const parsedJobConfig = job.jobConfig ? parseCrmJobConfig(job.jobConfig) : {};

    return {
      jobId: job.jobId,
      arenaDomain: job.arenaDomain,
      arenaUserEmail: job.arenaUserEmail,
      environment: job.environment,
      createdAt: job.createdAt,
      parsedJobConfig,
    };
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: jobConfigData,
  };
};

export const useShouldPollJob = (jobStatus?: DataStudioJobStatusEnum) => {
  return useMemo(() => {
    if (!jobStatus) return false;

    return jobStatus === "IN_PROGRESS" || jobStatus === "CREATED";
  }, [jobStatus]);
};
